/**
 * A deity's entity panel, opened from its row on the Taxonomy screen (CSR-124; doc 05-admin/10
 * §3.4).
 *
 * **Built from the spec, not a design.** No design frame exists for it; the owner ruled on
 * 2026-10-26 that design-less screens are built from the docs with the console's own components
 * and restyled later.
 *
 * The PATCH changes only the fields it carries, so Save sends only what the operator changed. The
 * parent must be another deity and never one of this one's descendants: the picker leaves out the
 * deity itself, and the server refuses a loop with a reason that is shown as it came back, as is a
 * content-lint refusal of an alias or the significance, which are public copy.
 */
import { useState } from 'react';
import { Kicker } from '../components/primitives';
import { Input, Select, Toggle } from '../components/forms';
import { ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, ScopeAction } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { useAxisValues } from '../lib/api/hooks/catalogue';
import { useDeityEntity, usePatchDeityEntity, type DeityEntity, type DeityEntityPatch } from '../lib/api/hooks/deityEntity';

/** The server's caps (`DeityEntityPatchIn`). */
export const MAX_ALIASES = 30;
const NO_PARENT = 'No parent — a deity in its own right';
const NOTE_STYLE = { color: 'var(--c-text-muted)', fontSize: 'var(--fs-admin-colhead)' } as const;

type Draft = { parent: number | null; aliases: string; wikipedia: string; significance: string; isPage: boolean };

/** One alias per line; trimmed, blanks dropped, each once ignoring case — as the server stores them. */
export function parseAliases(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const line of text.split('\n')) {
    const a = line.trim();
    if (a && !seen.has(a.toLowerCase())) {
      seen.add(a.toLowerCase());
      out.push(a);
    }
  }
  return out;
}

const orNull = (s: string) => (s.trim() ? s.trim() : null);

/** The PATCH body: only the fields that differ from what was read. */
export function entityPatch(row: DeityEntity, d: Draft): DeityEntityPatch {
  const patch: DeityEntityPatch = {};
  if (d.parent !== row.parent_deity_id) patch.parent_deity_id = d.parent;
  const aliases = parseAliases(d.aliases);
  if (JSON.stringify(aliases) !== JSON.stringify(row.aliases)) patch.aliases = aliases;
  if (orNull(d.wikipedia) !== row.wikipedia_title) patch.wikipedia_title = orNull(d.wikipedia);
  if (orNull(d.significance) !== row.significance) patch.significance = orNull(d.significance);
  if (d.isPage !== row.is_page) patch.is_page = d.isPage;
  return patch;
}

export function entityProblems(d: Draft): string[] {
  const out: string[] = [];
  const aliases = parseAliases(d.aliases);
  if (aliases.length > MAX_ALIASES) out.push(`At most ${MAX_ALIASES} aliases.`);
  for (const a of aliases) if (a.length > 80) out.push(`"${a.slice(0, 20)}…" is over 80 characters.`);
  if (d.wikipedia.trim().length > 200) out.push('The Wikipedia title is over 200 characters.');
  if (d.significance.trim().length > 20000) out.push('The significance is over 20,000 characters.');
  return out;
}

function EntityForm({ row }: { row: DeityEntity }) {
  const { scope, toast } = useConsole();
  const deities = useAxisValues('deities');
  const save = usePatchDeityEntity();
  const [confirming, setConfirming] = useState(false);
  const [draft, setDraft] = useState<Draft>({
    parent: row.parent_deity_id,
    aliases: row.aliases.join('\n'),
    wikipedia: row.wikipedia_title ?? '',
    significance: row.significance ?? '',
    isPage: row.is_page,
  });
  /* The panel opens only from a row of this same list, so it is already in the cache. */
  const others = deities.data!.items.filter((d) => d.id !== row.id);
  const parentName =
    draft.parent === null ? NO_PARENT : (others.find((d) => d.id === draft.parent)?.name_en ?? `Deity #${draft.parent}`);
  const options = [NO_PARENT, ...others.map((d) => d.name_en)];
  const patch = entityPatch(row, draft);
  const problems = entityProblems(draft);

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-sm)', padding: 'var(--sp-md)', borderTop: '1px solid var(--c-border)' }}>
      <Select
        density="admin"
        label={`Parent of ${row.name_en}`}
        options={options.includes(parentName) ? options : [parentName, ...options]}
        value={parentName}
        onChange={(v) => setDraft({ ...draft, parent: v === NO_PARENT ? null : others.find((d) => d.name_en === v)!.id })}
      />
      <Input
        density="admin"
        multiline
        label={`Aliases of ${row.name_en}`}
        value={draft.aliases}
        onChange={(v) => setDraft({ ...draft, aliases: v })}
        helper="One per line — public copy, content-linted when saved."
      />
      <Input density="admin" label={`Wikipedia title, ${row.name_en}`} value={draft.wikipedia} onChange={(v) => setDraft({ ...draft, wikipedia: v })} />
      <Input
        density="admin"
        multiline
        label={`Significance of ${row.name_en}`}
        value={draft.significance}
        onChange={(v) => setDraft({ ...draft, significance: v })}
        helper="Traditional and scriptural only — never an outcome a devotee can expect."
      />
      <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'center' }}>
        <Toggle label={`Builds a page, ${row.name_en}`} on={draft.isPage} onChange={(on) => setDraft({ ...draft, isPage: on })} />
        <span style={NOTE_STYLE}>{draft.isPage ? 'Earns a deity page at the next site build.' : 'A tag only — no page is built.'}</span>
      </div>
      <span style={NOTE_STYLE}>
        Demand: {row.demand_views_12m == null ? 'not measured yet' : `${row.demand_views_12m.toLocaleString('en-IN')} views in 12 months`}
        {row.demand_measured_on ? ` · measured ${row.demand_measured_on}` : ''}
      </span>
      {problems.length > 0 && (
        <ul aria-label="Fix before saving" style={{ margin: 0, color: 'var(--c-warn-text)' }}>
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      <div>
        <ScopeAction
          scope={scope}
          need="catalogue_content"
          size="sm"
          disabled={Object.keys(patch).length === 0 || problems.length > 0}
          loading={save.isPending}
          onClick={() => setConfirming(true)}
        >
          Save entity
        </ScopeAction>
      </div>
      {confirming && (
        // Aliases and the significance are public copy, and the page switch builds or drops a page.
        <ConfirmAction
          open
          title={`Save ${row.name_en}'s entity fields?`}
          phrase="SAVE"
          consequences={[
            'Aliases and the significance are public copy: they reach the website at the next site build, content-linted on save.',
            ...(patch.is_page === undefined
              ? []
              : [patch.is_page ? `${row.name_en} earns a deity page at the next site build.` : `${row.name_en}'s deity page is dropped at the next site build.`]),
          ]}
          confirmLabel="Save entity"
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            save.mutate(
              { id: row.id, patch },
              {
                onSuccess: () => toast({ tone: 'success', message: `${row.name_en} saved` }),
                onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
              },
            );
            setConfirming(false);
          }}
        />
      )}
    </div>
  );
}

export function DeityEntityPanel({ id }: { id: number }) {
  const query = useDeityEntity(id);
  if (query.isPending) return <LoadingState message="Reading the deity…" />;
  if (query.isError) return <ErrorState title="Could not read this deity" cause={errorMessage(query.error)} />;
  const row = query.data;
  return (
    <>
      <Kicker>
        {row.slug} · {row.deity_kind}
      </Kicker>
      <EntityForm key={JSON.stringify(row)} row={row} />
    </>
  );
}
